import Macy from "macy";
import imagesLoaded from "imagesloaded";
import NanoModal from "../modal.js";
import { BaseAjaxService } from "./BaseAjaxService.js";

/**
 * Base class for managing item display modals (interior and furniture)
 */
export class BaseModalManager {
  constructor(config) {
    this.config = {
      type: config.type, // 'items' or 'furniture'
      triggerSelector: config.triggerSelector,
      ajaxAction: config.ajaxAction,
      dataKey: config.dataKey,
      emptyMessage: config.emptyMessage,
      ...config,
    };

    this.ajaxService = new BaseAjaxService();
    this.init();
  }

  init() {
    const triggers = document.querySelectorAll(this.config.triggerSelector);

    triggers.forEach((trigger) => {
      const modal = new NanoModal("#" + trigger.getAttribute("aria-controls"), {
        onShow: () => this.handleModalShow(trigger),
        onClose: () => this.handleModalClose(trigger),
        closeOnEscape: true,
        closeOnOutsideClick: true,
        disableScrollShift: false,
        disableScroll: true,
        debug: false,
      });

      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        modal.showModal();
      });
    });
  }

  async handleModalShow(trigger) {
    const modalContent = document.querySelector(
      "#" + trigger.getAttribute("aria-controls") + " .modal-content"
    );

    if (!modalContent) return;

    try {
      const response = await this.ajaxService.request(this.config.ajaxAction, {
        security: this.ajaxService.nonce,
      });

      let items = response[this.config.dataKey];

      if (!items || items.length === 0) {
        modalContent.innerHTML = `<p>${this.config.emptyMessage}</p>`;
        return;
      }

      // Group items by main category
      let groupedItems = this.groupItemsByCategory(items);

      // For interior items, sort within each main category by the DOM order of the
      // right-side list to match the canonical CSV-derived ordering
      if (this.config.type === "items") {
        groupedItems = this.sortGroupedItemsByDomOrder(groupedItems);
      }

      // Generate HTML
      modalContent.innerHTML = this.generateModalHTML(groupedItems);

      // Initialize masonry layout
      this.initializeMasonry();
    } catch (error) {
      modalContent.innerHTML = "<p>Error fetching data</p>";
      console.error(error);
    }

    // Mark trigger as expanded
    this.updateTriggerState(trigger, true);
  }

  handleModalClose(trigger) {
    const modalContent = document.querySelector(
      "#" + trigger.getAttribute("aria-controls") + " .modal-content"
    );

    if (modalContent) {
      modalContent.innerHTML = "";
    }

    this.updateTriggerState(trigger, false);
  }

  updateTriggerState(trigger, isExpanded) {
    const triggers = document.querySelectorAll(this.config.triggerSelector);
    triggers.forEach((t) => {
      t.setAttribute("aria-expanded", isExpanded.toString());
    });
  }

  groupItemsByCategory(items) {
    return items.reduce((acc, item) => {
      const mainCat = item.main_cat;
      if (!acc[mainCat]) acc[mainCat] = [];
      acc[mainCat].push(item);
      return acc;
    }, {});
  }

  generateModalHTML(groupedItems) {
    const getItemDisplayName = (item) =>
      item.display_nimetus || item.nimetus || "";

    return Object.entries(groupedItems)
      .map(
        ([mainCat, items]) => `
        <div class="main-category pb-42">
          <h2 class="block font-normal text-22 font-heading mb-20">${mainCat}</h2>
          <div class="modal-lists-container">
            <div class="masonry-gallery">
              <ol class="image-list masonry">
                ${items
                  .map(
                    (item, index) => `
                  <li>
                    <div class="relative">
                      <span class="img-number">${index + 1}</span>
                      <img class="block masonry-img" src="${
                        item.pildi_url
                      }" alt="${item.nimetus}">
                    </div>
                  </li>
                `
                  )
                  .join("")}
              </ol>
            </div>
            <ol class="name-list">
              ${items
                .map(
                  (item, index) => `
                <li>
                  <div class="flex items-center">
                    <span class="list-number">${index + 1}</span>
                    <div>
                      <span class="subcategory font-medium text-14 block">${
                        item.sub_cat
                      }</span>
                      <span class="block font-normal">${getItemDisplayName(
                        item
                      )}</span>
                    </div>
                  </div>
                </li>
              `
                )
                .join("")}
            </ol>
          </div>
        </div>
      `
      )
      .join("");
  }

  /**
   * Create a mapping of item name (nimetus) to its index based on the DOM order
   * within the right-side "interior" list for each main category.
   * Returns an object keyed by main_cat_slug, whose values are maps nimetus -> index.
   */
  buildDomOrderIndex() {
    const result = {};
    const categoryContainers = document.querySelectorAll(
      "[data-list-item-with-cat]"
    );

    categoryContainers.forEach((container) => {
      const mainCatSlug = container.getAttribute("data-list-item-with-cat");
      if (!mainCatSlug) return;

      const items = container.querySelectorAll(".single-interior-item");
      const orderMap = {};

      let index = 0;
      items.forEach((itemEl) => {
        const button = itemEl.querySelector(".select-item-button");
        if (!button) return;
        const json = button.getAttribute("data-item");
        if (!json) return;
        try {
          const data = JSON.parse(json);
          const key = data?.interior_id || data?.nimetus;
          if (key && orderMap[key] === undefined) {
            orderMap[key] = index++;
          } else if (key) {
            // Ensure unique sequencing even if duplicates somehow occur
            orderMap[`${key}__${index}`] = index++;
          }
        } catch (e) {
          // ignore malformed
        }
      });

      result[mainCatSlug] = orderMap;
    });

    return result;
  }

  /**
   * Sort items within each main category to match the DOM order in the right-side list.
   * groupedItems: { [mainCatLabel: string]: Array<Item> }
   */
  sortGroupedItemsByDomOrder(groupedItems) {
    const domOrderIndex = this.buildDomOrderIndex();

    const sorted = {};
    Object.entries(groupedItems).forEach(([mainCatLabel, items]) => {
      if (!Array.isArray(items) || items.length === 0) {
        sorted[mainCatLabel] = items;
        return;
      }

      const mainCatSlug = items[0]?.main_cat_slug;
      const indexMap = mainCatSlug ? domOrderIndex[mainCatSlug] : null;

      if (!indexMap) {
        // No index for this category; keep original order
        sorted[mainCatLabel] = items;
        return;
      }

      const getIndex = (item) => {
        // Primary key: exact match by nimetus
        const key = item?.interior_id || item?.nimetus;
        if (key in indexMap) return indexMap[key];
        // Fallback: place unknown items at the end, sorted by name
        return Number.POSITIVE_INFINITY;
      };

      const itemsCopy = items.slice();
      itemsCopy.sort((a, b) => {
        const ia = getIndex(a);
        const ib = getIndex(b);
        if (ia === ib) {
          const an = (a?.nimetus || "").localeCompare(b?.nimetus || "");
          return an;
        }
        return ia - ib;
      });

      sorted[mainCatLabel] = itemsCopy;
    });

    return sorted;
  }

  initializeMasonry() {
    const masonryContainers = document.querySelectorAll(".masonry");

    masonryContainers.forEach((container) => {
      const macyInstance = Macy({
        container: container,
        columns: 3,
        margin: 10,
        breakAt: {
          1200: 3,
          940: 2,
        },
      });

      // Wait for images to load before recalculating layout
      imagesLoaded(container, () => {
        macyInstance.recalculate();
      });
    });
  }
}
